/**
 * In-page numpad for the mobile Stocktake detail page (§8.3).
 *
 * Design notes per the spec:
 *
 *   - Always visible on the bottom of the stocktake detail page on
 *     mobile (≤768px). Roughly 180px tall, ≥44px tap targets.
 *   - Keys: ``0`` … ``9``. **No decimal key**: the backend
 *     ``counted_qty`` is an integer (``Field(ge=0)`` on
 *     ``PatchLineRequest``, and the parent's ``onNumpadChange`` calls
 *     ``Math.round`` before committing). A "." key would let the
 *     operator type ``12.7``, see ``12.7`` in the preview, and then
 *     silently commit a rounded ``13``. We strip the key entirely
 *     rather than disable it, so the affordance never appears.
 *   - ⌫ (backspace) and C (clear) used to live in column 4 of rows 1+2;
 *     they've been hoisted into the parent's right action column so the
 *     numpad shrinks to a clean 3×4 digit grid and the destructive
 *     affordances (⌫, C) stack alongside the other auxiliary buttons
 *     (collapse, search, camera). The parent owns the wiring via plain
 *     ``onNumpadChange("")`` / ``onNumpadChange(numpadValue.slice(0,-1))``.
 *   - Input mode is **edit-by-tap**: each keypress mutates the running
 *     ``displayValue`` string. The parent commits the parsed number
 *     when the user moves to a different row (or presses ``Enter`` on
 *     a keyboard; not handled here — see §8.3 page logic).
 *   - Backend scan endpoint is the ADD path; this numpad's role is
 *     manual replace.
 *
 * Two digit layouts are supported, selectable via the ``layout`` prop:
 *
 *   CALCULATOR (default) — desktop-style 7-8-9 at the top:
 *
 *     Row 1: 7  8  9
 *     Row 2: 4  5  6
 *     Row 3: 1  2  3
 *     Row 4: 0(span 3)
 *
 *   PHONE — dial-pad style 1-2-3 at the top:
 *
 *     Row 1: 1  2  3
 *     Row 2: 4  5  6
 *     Row 3: 7  8  9
 *     Row 4: 0(span 3)
 *
 * The numpad is *uncontrolled* w.r.t. the page's state — it only emits
 * the next display string. The page decides when to actually PATCH
 * (typically on debounce or row-change). That keeps this component a
 * pure UI primitive and lets the page own all the network logic.
 *
 * **Important**: every click here is a synthetic ``MouseEvent``. The
 * global scanner listener on the parent page filters via
 * ``e.isTrusted === true``, so these clicks DO NOT fire the scanner.
 * If you ever switch this away from <button onClick> (e.g. to a
 * gesture handler), preserve that property — otherwise the scanner
 * listener will eat numpad keystrokes as USB-wedge barcodes.
 */

import { Button } from "antd";

export type NumpadLayout = "calculator" | "phone";

export interface NumpadProps {
  /** Current display string. Empty string means "no value set yet". */
  value: string;
  /** Emitted on every key press with the next display string. */
  onChange: (next: string) => void;
  /** Disables every key. Used when the page is blocked (offline modal,
   * stocktake finalized, etc.). */
  disabled?: boolean;
  /** Digit ordering. Defaults to ``"calculator"`` (7-8-9 on top). */
  layout?: NumpadLayout;
}

type Key =
  | "0"
  | "1"
  | "2"
  | "3"
  | "4"
  | "5"
  | "6"
  | "7"
  | "8"
  | "9";

const KEY_LABELS: Record<Key, string> = {
  "0": "0",
  "1": "1",
  "2": "2",
  "3": "3",
  "4": "4",
  "5": "5",
  "6": "6",
  "7": "7",
  "8": "8",
  "9": "9",
};

const applyKey = (current: string, key: Key): string => {
  // Digit. Don't allow leading-zero junk like "0042" — collapse to "42".
  if (current === "0") return key;
  return current + key;
};

export const Numpad = ({
  value,
  onChange,
  disabled = false,
  layout = "calculator",
}: NumpadProps) => {
  const press = (key: Key) => {
    if (disabled) return;
    onChange(applyKey(value, key));
  };

  // Digit triples for rows 1-3 depend on layout. Row 4 (the wide "0")
  // is identical in both.
  const digitRows: [Key, Key, Key][] =
    layout === "phone"
      ? [
          ["1", "2", "3"],
          ["4", "5", "6"],
          ["7", "8", "9"],
        ]
      : [
          ["7", "8", "9"],
          ["4", "5", "6"],
          ["1", "2", "3"],
        ];

  return (
    <div
      data-testid="numpad"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(3, 1fr)",
        // No outer padding/frame: the keys span the full column so the
        // keypad is exactly as wide as the "=" button + info bar above it,
        // and the big digits sit edge-to-edge with minimal surrounding space.
        gap: 6,
      }}
    >
      {/* Row 1 */}
      <NumpadKey k={digitRows[0][0]} onPress={press} disabled={disabled} />
      <NumpadKey k={digitRows[0][1]} onPress={press} disabled={disabled} />
      <NumpadKey k={digitRows[0][2]} onPress={press} disabled={disabled} />
      {/* Row 2 */}
      <NumpadKey k={digitRows[1][0]} onPress={press} disabled={disabled} />
      <NumpadKey k={digitRows[1][1]} onPress={press} disabled={disabled} />
      <NumpadKey k={digitRows[1][2]} onPress={press} disabled={disabled} />
      {/* Row 3 */}
      <NumpadKey k={digitRows[2][0]} onPress={press} disabled={disabled} />
      <NumpadKey k={digitRows[2][1]} onPress={press} disabled={disabled} />
      <NumpadKey k={digitRows[2][2]} onPress={press} disabled={disabled} />
      {/* Row 4: 0 spans all 3 cols. */}
      <div style={{ gridColumn: "span 3" }}>
        <NumpadKey k="0" onPress={press} disabled={disabled} fullWidth />
      </div>
    </div>
  );
};

interface NumpadKeyProps {
  k: Key;
  onPress: (key: Key) => void;
  disabled: boolean;
  fullWidth?: boolean;
}

const NumpadKey = ({ k, onPress, disabled, fullWidth }: NumpadKeyProps) => (
  <Button
    block
    type="primary"
    onClick={() => onPress(k)}
    disabled={disabled}
    style={{
      height: 46,
      fontSize: 30,
      fontWeight: 600,
      width: fullWidth ? "100%" : undefined,
    }}
  >
    {KEY_LABELS[k]}
  </Button>
);

export default Numpad;
